/**
 * Tool Top Users Modal
 * Shows the top users for a specific tool
 */

import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { X, Users as UsersIcon, RefreshCw, Search } from 'lucide-react';
import { filterDashboardUsers } from '../../../utils/dashboardUserFilters';
import '../AIGarage.css';

const ToolTopUsersModal = ({ isOpen, tool, topUsers, loading, onClose, onUserClick, getUserInitials, getUserColor }) => {
  const [searchQuery, setSearchQuery] = useState('');

  // Hide excluded product-dev emails (UI only), then apply search
  const filteredUsers = useMemo(() => {
    const visibleUsers = filterDashboardUsers(topUsers || []);
    if (!searchQuery.trim()) return visibleUsers;

    const query = searchQuery.toLowerCase();
    return visibleUsers.filter(user =>
      (user.userName || '').toLowerCase().includes(query) ||
      (user.userEmail || '').toLowerCase().includes(query)
    );
  }, [topUsers, searchQuery]);

  if (!isOpen || !tool) return null;

  const modalContent = (
    <div className="garage-modal-overlay" onClick={onClose}>
      <div className="garage-modal-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="garage-modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
            {tool.imageUrl && (
              <img
                src={tool.imageUrl}
                alt={tool.toolTitle}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '8px',
                  objectFit: 'cover'
                }}
              />
            )}
            <div style={{ flex: 1 }}>
              <h2 className="garage-modal-title" style={{ marginBottom: '4px' }}>
                {tool.toolTitle}
              </h2>
              <p style={{ fontSize: '13px', color: 'var(--garage-text-3)', margin: 0 }}>
                {tool.persona || tool.toolType}
              </p>
            </div>
          </div>
          <button className="garage-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="garage-modal-body" style={{ padding: 0 }}>
          {/* Search Bar */}
          <div style={{
            padding: '16px 24px',
            backgroundColor: 'var(--garage-surface)',
            borderBottom: '1px solid var(--garage-border)'
          }}>
            <div style={{ position: 'relative' }}>
              <Search size={18} style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--garage-text-3)'
              }} />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px 10px 40px',
                  border: '1px solid var(--garage-border)',
                  borderRadius: '8px',
                  fontSize: '14px',
                  backgroundColor: 'var(--garage-bg)',
                  color: 'var(--garage-text)'
                }}
              />
            </div>
          </div>

          {/* Section Header */}
          <div style={{
            padding: '12px 24px',
            backgroundColor: 'var(--garage-bg-2)',
            borderBottom: '1px solid var(--garage-border)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <UsersIcon size={18} style={{ color: 'var(--garage-green)' }} />
            <span style={{
              fontSize: '13px',
              fontWeight: '600',
              color: 'var(--garage-text-2)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Top Users ({filteredUsers.length})
            </span>
          </div>

          {/* Users List */}
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--garage-text-3)' }}>
                <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite' }} />
                <div style={{ marginTop: '12px', fontSize: '14px' }}>Loading users...</div>
              </div>
            ) : filteredUsers && filteredUsers.length > 0 ? (
              <div>
                {filteredUsers.map((user, idx) => (
                  <div
                    key={user.userEmail}
                    onClick={() => onUserClick(user)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      padding: '16px 24px',
                      borderBottom: idx < filteredUsers.length - 1 ? '1px solid var(--garage-border-2)' : 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--garage-bg-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* Rank */}
                    <div style={{
                      width: '32px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--garage-text-3)',
                      textAlign: 'center'
                    }}>
                      {idx + 1}
                    </div>

                    {/* Avatar */}
                    <div
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '50%',
                        backgroundColor: getUserColor(idx),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: '600',
                        fontSize: '16px',
                        flexShrink: 0
                      }}
                    >
                      {getUserInitials(user.userEmail)}
                    </div>

                    {/* User Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: 'var(--garage-text)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {user.userName}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--garage-text-3)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginTop: '2px'
                      }}>
                        {user.userEmail}
                      </div>
                    </div>

                    {/* Sessions */}
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        fontSize: '20px',
                        fontWeight: '700',
                        color: 'var(--garage-blue)'
                      }}>
                        {user.sessions}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        color: 'var(--garage-text-3)',
                        marginTop: '2px'
                      }}>
                        sessions
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : searchQuery.trim() ? (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--garage-text-3)', fontSize: '14px' }}>
                No users found matching "{searchQuery}"
              </div>
            ) : (
              <div style={{ padding: '48px', textAlign: 'center', color: 'var(--garage-text-3)', fontSize: '14px' }}>
                No user data available for this tool
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default ToolTopUsersModal;
